// Fase 17.3, Plano 05 — "Roteiros das peças": a entrada do PCP (D-13). Mostra quais peças não têm roteiro, quais vieram do
// CargaMáquina, quais foram ajustadas no sistema, quais faltam área/cor e quais têm lote esperando. Filtros, ordem e
// paginação são do servidor (GET /api/v1/roteiros/pecas); os filtros ficam na URL (sem Web Storage, SEC-001).
import { useCallback, useEffect, useState } from 'react';
import type { FiltrosPecasRoteiroDto, ListaPecasRoteiroDto, ModeloRoteiroResumoDto, PecaRoteiroListaItemDto, SituacaoRoteiroPeca } from '@pieltec/contracts';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { CampoSelect } from '../../../components/ui/campo-select';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { TablePagination } from '../../../components/ui/table-pagination';
import { linkDaFichaDaPeca, ROTULO_PRODUTO_SEM_CADASTRO } from '../../../lib/roteiros/cadastro-peca';
import { aplicarFiltros, filtrosDaUrl, haFiltrosAtivos, ROTULOS_SITUACAO, TAMANHO_PAGINA_PADRAO, urlDosFiltros } from '../../../lib/roteiros/filtros';
import { listarModelos, listarPecas } from '../../../lib/roteiros/roteiros-api';
import { obterSessaoAtual } from '../../../lib/session';
import { ListaProdutosSemCadastro } from './produtos-sem-cadastro';
import { CadastroPecaAtalho } from '../../cadastros/cadastro-peca-atalho';

const CABECALHO = 'px-2 py-2 text-xs font-semibold uppercase tracking-wider';
const TODAS_AS_SITUACOES = '';
const TODOS_OS_MODELOS = '';
/** Não é uma situação do roteiro: troca a tabela pela lista de itens de PV sem peça cadastrada (D-25). */
const VALOR_SEM_CADASTRO = 'SEM_CADASTRO';
const OPCOES_SITUACAO = [
  { value: TODAS_AS_SITUACOES, label: 'Todas' },
  ...(Object.entries(ROTULOS_SITUACAO) as [SituacaoRoteiroPeca, string][]).map(([value, label]) => ({ value, label })),
];

function BadgeSituacao({ situacao }: { readonly situacao: SituacaoRoteiroPeca }) {
  const variante = situacao === 'SEM_ROTEIRO' ? 'destructive' : situacao === 'IMPORTADO' ? 'secondary' : 'outline';
  return <Badge variant={variante}>{ROTULOS_SITUACAO[situacao]}</Badge>;
}

function textoLotes(total: number): string {
  return total === 1 ? '1 lote aguardando roteiro' : `${total} lotes aguardando roteiro`;
}

function PendenciasDaPeca({ peca, href }: { readonly peca: PecaRoteiroListaItemDto; readonly href: string }) {
  if (!peca.dadosIncompletos) return <span className="text-muted-foreground">Completos</span>;
  return <div className="space-y-1 text-xs"><p>{peca.dadosTecnicosPendentes?.length ? `Não informado no cadastro: ${peca.dadosTecnicosPendentes.join(' e ')}.` : 'Há dados técnicos pendentes; consulte os campos na ficha.'}</p><a className="inline-flex min-h-11 items-center underline" onClick={(evento) => evento.stopPropagation()} href={`${href}#dados-da-peca`}>Conferir e preencher na ficha</a></div>;
}

function LinhaPeca({ peca, cadastro, consulta }: { readonly peca: PecaRoteiroListaItemDto; readonly cadastro: boolean; readonly consulta: string }) {
  const aguardando = peca.lotesAguardandoRoteiro > 0;
  const fichaHref = `${cadastro ? '/cadastros/pecas' : '/pcp/roteiros'}/${encodeURIComponent(peca.materialId)}${consulta ? `?${consulta}` : ''}`;
  if (cadastro) return <tr className="border-t align-top">
    <td className="px-4 py-4"><a className="font-semibold underline-offset-4 hover:underline" href={fichaHref}>{peca.codigo}</a><div>{peca.nome}</div>{peca.dadosIncompletos ? <PendenciasDaPeca peca={peca} href={fichaHref} /> : null}</td>
    <td className="px-4 py-4">{peca.clientes?.length ? peca.clientes.map((cliente) => <div key={`${cliente.clienteId}:${cliente.codigoItemCliente}`}>{cliente.clienteNome}<span className="block text-xs text-muted-foreground">Código do cliente: {cliente.codigoItemCliente}</span></div>) : <span className="text-muted-foreground">{peca.clienteLegado ? `${peca.clienteLegado} (legado, vínculo a confirmar)` : 'Vínculo não confirmado'}</span>}</td>
    <td className="px-4 py-4"><BadgeSituacao situacao={peca.situacao} />{peca.versaoVigente !== null ? <div className="text-xs text-muted-foreground">Roteiro v{peca.versaoVigente}</div> : null}</td>
    <td className="px-4 py-4"><div className="flex flex-col gap-2"><Button asChild variant="outline" size="sm"><a href={`${fichaHref}#dados-da-peca`}>Dados da peça</a></Button><Button asChild variant="outline" size="sm"><a href={`${fichaHref}#roteiro`}>Roteiro</a></Button></div></td>
  </tr>;
  return (
    <tr className={`border-t ${aguardando ? 'bg-destructive/10' : ''}`}>
      <td className="px-2 py-2 font-semibold">
        <a className="underline-offset-4 hover:underline" href={fichaHref}>{peca.codigo}</a>
        {peca.codigosLegados.length > 0 ? <div className="text-xs font-normal text-muted-foreground">{peca.codigosLegados.join(', ')}</div> : null}
      </td>
      <td className="px-2 py-2">{peca.nome}</td>
      <td className="px-2 py-2"><BadgeSituacao situacao={peca.situacao} /></td>
      <td className="px-2 py-2">
        {peca.modelo ? (
          <div className="flex flex-wrap items-center gap-2">
            <span>{peca.modelo.nome}</span>
            {peca.difereDoModelo ? <Badge variant="outline">Difere do modelo</Badge> : null}
          </div>
        ) : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-2 py-2">{peca.versaoVigente === null ? <span className="text-muted-foreground">—</span> : `v${peca.versaoVigente}`}</td>
      <td className="px-2 py-2"><PendenciasDaPeca peca={peca} href={fichaHref} /></td>
      <td className="px-2 py-2">{peca.clientes?.length ? peca.clientes.map((cliente) => <div key={`${cliente.clienteId}:${cliente.codigoItemCliente}`}>{cliente.clienteNome}<span className="block text-xs text-muted-foreground">Código do cliente: {cliente.codigoItemCliente}</span></div>) : <span className="text-muted-foreground">{peca.clienteLegado ? `${peca.clienteLegado} (legado, vínculo a confirmar)` : 'Vínculo não confirmado'}</span>}</td>
      <td className="px-2 py-2">{aguardando ? <span className="font-semibold text-destructive">{textoLotes(peca.lotesAguardandoRoteiro)}</span> : <span className="text-muted-foreground">—</span>}</td>
    </tr>
  );
}

export function PecasProdutosPage() { return <RoteirosListaPage cadastro />; }

export function RoteirosListaPage({ cadastro = false }: { readonly cadastro?: boolean }) {
  const [nomeContexto] = useState(() => new URLSearchParams(window.location.search).get('clienteNome') ?? 'Cliente selecionado');
  const [filtros, setFiltros] = useState<FiltrosPecasRoteiroDto>(() => ({ ...filtrosDaUrl(window.location.search), ...(cadastro ? { catalogo: true } : {}) }));
  const [buscaDigitada, setBuscaDigitada] = useState(filtros.busca ?? '');
  const [clienteDigitado, setClienteDigitado] = useState(filtros.cliente ?? '');
  const [modelos, setModelos] = useState<readonly ModeloRoteiroResumoDto[]>([]);
  const [dados, setDados] = useState<ListaPecasRoteiroDto | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [semCadastro, setSemCadastro] = useState(false);
  const chaveConsulta = urlDosFiltros(filtros);

  // A autorização real é do servidor; aqui o botão de importar só aparece para quem pode (D-16).
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo && sessao.autenticado) setPermissoes(sessao.usuario.permissions);
    });
    return () => { vivo = false; };
  }, []);

  const carregar = useCallback(async (sinal?: { vivo: boolean }) => {
    setCarregando(true);
    setErro(false);
    try {
      const consulta = filtrosDaUrl(chaveConsulta);
      const resposta = await listarPecas(consulta);
      // "Cadastrar roteiro" vem da Central de Pendências com ?lote=: se o lote é de uma peça só, abre a ficha direto.
      const unica = resposta.itens[0];
      if (consulta.loteId && resposta.total === 1 && unica && (!sinal || sinal.vivo)) {
        window.location.replace(linkDaFichaDaPeca(unica.materialId));
        return;
      }
      if (!sinal || sinal.vivo) setDados(resposta);
    } catch {
      if (!sinal || sinal.vivo) setErro(true);
    } finally {
      if (!sinal || sinal.vivo) setCarregando(false);
    }
  }, [chaveConsulta]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => { sinal.vivo = false; };
  }, [carregar]);

  // Filtros na URL: recarregar ou compartilhar o link preserva o recorte (replaceState não empilha histórico).
  useEffect(() => {
    const destino = `${window.location.pathname}${chaveConsulta ? `?${chaveConsulta}` : ''}`;
    if (destino !== `${window.location.pathname}${window.location.search}`) window.history.replaceState(null, '', destino);
  }, [chaveConsulta]);

  // A busca espera o usuário parar de digitar antes de consultar o servidor.
  useEffect(() => {
    const temporizador = window.setTimeout(() => {
      const nova = buscaDigitada.trim();
      setFiltros((atual) => ((atual.busca ?? '') === nova ? atual : aplicarFiltros(atual, { busca: nova || undefined })));
    }, 300);
    return () => window.clearTimeout(temporizador);
  }, [buscaDigitada]);

  // A lista de modelos e pequena (ate 200) e so alimenta o filtro; falhar aqui nao impede de usar a lista.
  useEffect(() => {
    let vivo = true;
    void listarModelos().then((lista) => { if (vivo) setModelos(lista); }).catch(() => undefined);
    return () => { vivo = false; };
  }, []);

  // O cliente tambem espera o usuario parar de digitar.
  useEffect(() => {
    const temporizador = window.setTimeout(() => {
      const novo = clienteDigitado.trim();
      setFiltros((atual) => ((atual.cliente ?? '') === novo ? atual : aplicarFiltros(atual, { cliente: novo || undefined })));
    }, 300);
    return () => window.clearTimeout(temporizador);
  }, [clienteDigitado]);

  const alterar = (mudanca: Partial<FiltrosPecasRoteiroDto>): void => setFiltros((atual) => aplicarFiltros(atual, mudanca));
  const opcoesModelo = [
    { value: TODOS_OS_MODELOS, label: 'Todos' },
    ...modelos.map((modelo) => ({ value: modelo.id, label: modelo.nome })),
  ];
  const limparTudo = (): void => { setBuscaDigitada(''); setClienteDigitado(''); setFiltros({ ...(cadastro ? { catalogo: true } : {}), ...(filtros.clienteId ? { clienteId: filtros.clienteId } : {}) }); };
  const podeImportar = permissoes.includes('roteiros:gerenciar');
  const podeVerSemCadastro = permissoes.includes('pv:ler');
  const podeCadastrarPeca = podeImportar && permissoes.includes('pv:editar');
  const opcoesSituacao = podeVerSemCadastro ? [...OPCOES_SITUACAO, { value: VALOR_SEM_CADASTRO, label: ROTULO_PRODUTO_SEM_CADASTRO }] : OPCOES_SITUACAO;
  const total = dados?.total ?? 0;
  const tamanhoPagina = dados?.tamanhoPagina ?? filtros.tamanhoPagina ?? TAMANHO_PAGINA_PADRAO;
  const pagina = filtros.pagina ?? 1;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-[28px] font-semibold leading-tight">{cadastro ? 'Peças / Produtos' : 'Roteiros das peças'}</h1>
              <p className="text-sm text-muted-foreground">{cadastro ? 'Consulte a peça, seus clientes, dados técnicos e roteiro na mesma ficha.' : 'Veja quais peças já têm roteiro, quais vieram do CargaMáquina e quais ainda precisam de ajuste.'}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild type="button" variant="outline"><a href="/pcp/roteiros/modelos">Modelos de roteiro</a></Button>
              <Button asChild type="button" variant="outline"><a href="/pcp/roteiros/tintas">Tintas e rendimento</a></Button>
              {podeImportar ? (
                <Button asChild type="button" variant="outline"><a href="/pcp/roteiros/importar">Importar do CargaMáquina</a></Button>
              ) : null}
              {podeImportar ? (
                <Button asChild type="button" variant="outline"><a href="/pcp/roteiros/dados-tecnicos">Importar área e gancheira</a></Button>
              ) : null}
            </div>
          </div>
          {cadastro && filtros.clienteId ? <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-4 text-sm"><span>Peças de {dados?.itens.flatMap((peca) => peca.clientes ?? []).find((cliente) => cliente.clienteId === filtros.clienteId)?.clienteNome ?? nomeContexto} · vínculos confirmados</span><Button asChild variant="outline" size="sm"><a href="/cadastros/pecas">Ver todos os clientes</a></Button></div> : null}
          {cadastro && podeCadastrarPeca ? <CadastroPecaAtalho clienteId={filtros.clienteId} clienteNome={dados?.itens.flatMap((peca) => peca.clientes ?? []).find((cliente) => cliente.clienteId === filtros.clienteId)?.clienteNome ?? nomeContexto} /> : null}
          <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-1">
              <label htmlFor="roteiros-busca" className="text-sm font-semibold">Buscar peça</label>
              <Input
                id="roteiros-busca"
                type="search"
                placeholder="Código, nome ou código legado"
                value={buscaDigitada}
                onChange={(evento) => setBuscaDigitada(evento.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {cadastro ? 'Busque por código, nome ou código legado. Produtos sem roteiro também aparecem no catálogo.' : 'Sem busca, a lista mostra as peças com roteiro, lote, PV ou vínculo de cliente. Busque pelo código para achar qualquer material.'}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="roteiros-situacao" className="text-sm font-semibold">Situação</label>
              <CampoSelect
                id="roteiros-situacao"
                value={semCadastro ? VALOR_SEM_CADASTRO : (filtros.situacao ?? TODAS_AS_SITUACOES)}
                onValueChange={(valor) => {
                  setSemCadastro(valor === VALOR_SEM_CADASTRO);
                  alterar({ situacao: valor === TODAS_AS_SITUACOES || valor === VALOR_SEM_CADASTRO ? undefined : (valor as SituacaoRoteiroPeca) });
                }}
                options={opcoesSituacao}
              />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
            <div className="flex flex-col gap-1">
              <label htmlFor="roteiros-modelo" className="text-sm font-semibold">Modelo</label>
              <CampoSelect
                id="roteiros-modelo"
                value={filtros.modeloId ?? TODOS_OS_MODELOS}
                onValueChange={(valor) => alterar({ modeloId: valor === TODOS_OS_MODELOS ? undefined : valor })}
                options={opcoesModelo}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="roteiros-cliente" className="text-sm font-semibold">Cliente</label>
              <Input
                id="roteiros-cliente"
                type="search"
                placeholder="Nome ou código do cliente"
                value={clienteDigitado}
                onChange={(evento) => setClienteDigitado(evento.target.value)}
              />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label htmlFor="roteiros-incompletos" className="flex items-center gap-2 text-sm">
                <Checkbox id="roteiros-incompletos" checked={filtros.incompletos === true} onCheckedChange={(marcado) => alterar({ incompletos: marcado === true ? true : undefined })} />
                Só dados incompletos
              </label>
              <label htmlFor="roteiros-aguardando" className="flex items-center gap-2 text-sm">
                <Checkbox id="roteiros-aguardando" checked={filtros.aguardandoRoteiro === true} onCheckedChange={(marcado) => alterar({ aguardandoRoteiro: marcado === true ? true : undefined })} />
                Só aguardando roteiro
              </label>
              <label htmlFor="roteiros-sem-tinta" className="flex items-center gap-2 text-sm">
                <Checkbox id="roteiros-sem-tinta" checked={filtros.semTintaDaPeca === true} onCheckedChange={(marcado) => alterar({ semTintaDaPeca: marcado === true ? true : undefined })} />
                Só pintadas sem tinta da peça
              </label>
            </div>
          </div>
          {filtros.loteId ? (
            <div role="status" className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted px-4 py-2 text-sm">
              <span>Peça do lote selecionado na Central de Pendências</span>
              <Button type="button" variant="outline" size="sm" onClick={() => alterar({ loteId: undefined })}>Limpar filtro</Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {semCadastro ? (
        <ListaProdutosSemCadastro podeCadastrar={podeCadastrarPeca} busca={filtros.busca} clienteId={filtros.clienteId} />
      ) : erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-destructive">Não foi possível carregar as peças. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>Tentar novamente</Button>
          </CardContent>
        </Card>
      ) : carregando && dados === null ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando peças">
          {Array.from({ length: 8 }).map((_, indice) => <Skeleton key={indice} className="h-8 w-full" />)}
        </div>
      ) : (dados?.itens.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-sm font-semibold">Nenhuma peça encontrada com estes filtros.</p>
            {haFiltrosAtivos(filtros) ? <Button type="button" variant="outline" onClick={limparTudo}>Limpar filtros</Button> : null}
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <div className="overflow-x-auto" aria-busy={carregando}>
            <table className="w-full text-sm">
              <caption className="sr-only">Peças e situação do roteiro</caption>
              <thead className="bg-muted text-left">
                <tr>
                  {(cadastro ? ['Peça / Produto', 'Cliente / código do cliente', 'Situação', 'Acessos'] : ['Código', 'Peça', 'Situação', 'Modelo', 'Versão', 'Dados', 'Cliente / código do cliente', 'Lotes aguardando']).map((titulo) => (
                    <th key={titulo} scope="col" className={CABECALHO}>{titulo}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{dados?.itens.map((peca) => <LinhaPeca key={peca.materialId} peca={peca} cadastro={cadastro} consulta={chaveConsulta} />)}</tbody>
            </table>
          </div>
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={total}
            itemLabel="peças"
            onPageChange={(nova) => alterar({ pagina: nova })}
            onPageSizeChange={(novo) => alterar({ tamanhoPagina: novo })}
          />
        </Card>
      )}
    </div>
  );
}
